import { EventBus } from './events';
import { ConstructionSystem } from './ConstructionSystem';
import { CropSystem } from './CropSystem';
import { EconomySystem } from './EconomySystem';
import { EnergySystem } from './EnergySystem';
import { MillSystem } from './MillSystem';
import { MailboxSystem } from './MailboxSystem';
import { PlotSystem } from './PlotSystem';
import { ProgressionSystem } from './ProgressionSystem';
import { StorageSystem } from './StorageSystem';
import { TreeSystem } from './TreeSystem';
import { TruckSystem } from './TruckSystem';
import {
  BuildingType, PropKind, SeedOrderLine, SeedType, SimState, ToolId,
  CargoKind, CargoCounts, CARGO_KINDS, emptyCargo,
  DAY_LENGTH, SEASONS, Season, TIME_INITIAL_OFFSET,
  XP_REWARDS, SEED_MIN_PER_TILE, SEED_MAX_PER_TILE, SEED_COSTS, STONE_BUY_PRICE, PLOT_SUB_DIM, GROW_MUL_JITTER,
  // Calendar layer — see types.ts for the full GameDate contract and
  // forward-looking CropSeasonality type.
  Month, MONTHS, MONTH_DAYS, MONTH_SEASON, MONTH_FIRST_DOY,
  Weekday, WEEKDAYS, DAYS_PER_WEEK, DAYS_PER_YEAR, GAME_START_DOY,
  GameDate,
} from './types';
import { makeInitialState, defaultPropLayout } from './state';

/**
 * Sim — sole owner of mutable game state. UI/render read state, listen
 * to bus events, and call high-level intent methods. The sim itself
 * has no DOM/THREE dependency.
 */
export class Sim {
  state: SimState;
  bus = new EventBus();
  /** Throttle helper: last sim-time we emitted the weed-blocks-hoe hint. */
  private lastWeedHintAt = -10;
  /** Throttle helper: last sim-time we emitted the under-seeded hint. */
  private lastSeedHintAt = -10;
  /** Throttle helper: last sim-time we emitted a seed-blocked toast (no
   *  furrow / sub-cell full / no seeds in inventory). Stops drag-paint
   *  through unworkable tiles from spamming the UI. */
  private lastSeedBlockedAt = -10;
  /** Session bookkeeping for the 60% sickle auto-harvest: the count of
   *  stage-4 (ripe) seeds in the plot when the player started cutting,
   *  keyed by plotIdx. Reset whenever the plot is re-entered. Used to
   *  decide "have we now cut ≥60% of what was ripe? finish the rest." */
  private sickleSession = new Map<number, number>();

  constructor(initial?: SimState) {
    this.state = initial ?? makeInitialState();
    // Zoom is transient — always start in overview, even if a save somehow
    // captured it mid-zoom.
    this.state.zoom = { active: false, c: 0, r: 0 };
    // Backfill platform for saves predating the depot-platform feature.
    if (!this.state.truck.platform) {
      this.state.truck.platform = emptyCargo();
    }
    // Backfill new cargo kinds (mushroom / berry / acorn / cornFlour) for
    // saves that predate the expanded cargo set. Missing keys default to 0
    // without clobbering anything the player already has loaded.
    const fillCargo = (c: Partial<CargoCounts>) => {
      const z = emptyCargo();
      for (const k of CARGO_KINDS) if (c[k] === undefined) (c as any)[k] = z[k];
    };
    fillCargo(this.state.truck.cargo as Partial<CargoCounts>);
    fillCargo(this.state.truck.platform as Partial<CargoCounts>);
    // Backfill prop layout for saves predating the moveable-props feature.
    if (!this.state.props) this.state.props = defaultPropLayout();
    // Move mode is always transient — never start a session mid-relocation.
    this.state.moving = null;
    // Sickle (a.k.a. scythe internally) is part of the basic plot workflow
    // now; old saves that locked it behind level 3 should unlock it.
    if (this.state.prog?.unlocked) {
      this.state.prog.unlocked.scythe = true;
      this.state.prog.unlocked.pickaxe = true;
    }
    // Plot VIEW is transient — always start in overview.
    this.state.plotInView = false;
    this.state.activePlotIdx = null;
    this.state.editingPlot = false;
    this.state.placingBerry = false;
    // Backfill plots[] (and migrate any legacy single-plot save with
    // activePlot/weeds folded into the plot object).
    if (!Array.isArray(this.state.plots)) this.state.plots = [];
    if (!Array.isArray(this.state.weeds)) this.state.weeds = [];
    // Free-tile sub-cell arrays — sub-cell work on tiles outside any
    // adopted plot. Backfill to empty for v6 saves on load (v7 migration).
    if (!Array.isArray(this.state.freeTilled)) this.state.freeTilled = [];
    if (!Array.isArray(this.state.freeSeeds))  this.state.freeSeeds  = [];
    if (!Array.isArray(this.state.freeWet))    this.state.freeWet    = [];
    const legacy = (this.state as any).activePlot;
    if (legacy && legacy.tiles) {
      // Move the legacy plot's per-sub-cell data into a new PlotSession,
      // and hoist its weeds into the world-wide weed list.
      this.state.plots.push({
        tiles: legacy.tiles,
        tilled: legacy.tilled ?? [],
        seeds: legacy.seeds ?? [],
        wet: legacy.wet ?? [],
      });
      if (Array.isArray(legacy.weeds)) {
        for (const w of legacy.weeds) this.state.weeds.push(w);
      }
      delete (this.state as any).activePlot;
    }
    // Seed world-wide weeds on first launch — every grass tile gets a
    // sprinkle of weed tufts so the world reads "wild" before any farming.
    if (this.state.weeds.length === 0) {
      this.seedWorldWeeds();
    }
    // Version 5 sub-cell migration (legacy 4×4 → 8×8).
    if ((this.state.version ?? 0) < 5) {
      for (const p of this.state.plots) {
        const arrs: { subC: number; subR: number }[][] = [p.tilled, p.seeds, p.wet];
        for (const arr of arrs) for (const e of arr) { e.subC *= 2; e.subR *= 2; }
        for (const s of p.seeds) {
          if (s.ox === undefined) s.ox = 0;
          if (s.oz === undefined) s.oz = 0;
          if (s.growMul === undefined) s.growMul = 1.0;
        }
      }
      for (const w of this.state.weeds) { w.subC *= 2; w.subR *= 2; }
    }
    // Version 6 sub-cell migration (8×8 → 16×16). Doubles every sub-cell
    // coord again so the player's old work lines up on the finer grid.
    if ((this.state.version ?? 0) < 6) {
      for (const p of this.state.plots) {
        const arrs: { subC: number; subR: number }[][] = [p.tilled, p.seeds, p.wet];
        for (const arr of arrs) for (const e of arr) { e.subC *= 2; e.subR *= 2; }
      }
      for (const w of this.state.weeds) { w.subC *= 2; w.subR *= 2; }
    }
    // Version 7: free-tile sub-cell arrays. No data migration needed — just
    // ensure the empty arrays exist (handled by the array backfills above).
    // Version 8: sub-cell grid REVERTED from 16×16 back to 8×8 at user
    // request. Halve every existing sub-cell coord (clamped to 0..7) so
    // the player's saved work lines up on the smaller grid. Some entries
    // may collide (e.g. coords 0 and 1 both → 0); tilled/wet stay merged
    // by their position-key dedup elsewhere, seeds just become slightly
    // denser per sub-cell which is benign.
    if ((this.state.version ?? 0) < 8) {
      const halve = (n: number) => Math.max(0, Math.min(7, Math.floor(n / 2)));
      for (const p of this.state.plots) {
        for (const e of p.tilled) { e.subC = halve(e.subC); e.subR = halve(e.subR); }
        for (const e of p.seeds)  { e.subC = halve(e.subC); e.subR = halve(e.subR); }
        for (const e of p.wet)    { e.subC = halve(e.subC); e.subR = halve(e.subR); }
      }
      for (const w of this.state.weeds) { w.subC = halve(w.subC); w.subR = halve(w.subR); }
      if (Array.isArray(this.state.freeTilled)) for (const e of this.state.freeTilled) { e.subC = halve(e.subC); e.subR = halve(e.subR); }
      if (Array.isArray(this.state.freeSeeds))  for (const e of this.state.freeSeeds)  { e.subC = halve(e.subC); e.subR = halve(e.subR); }
      if (Array.isArray(this.state.freeWet))    for (const e of this.state.freeWet)    { e.subC = halve(e.subC); e.subR = halve(e.subR); }
    }
    // Version 9: weed thinning. Old saves were seeded at the dense
    // pre-fix density (4-10 per tile in v5/6, then 1-3 per 35% of tiles
    // in v7/8). Keep only ~1 in 4 of the existing weeds so the world
    // matches the new sparse density without forcing the player to pluck
    // hundreds of legacy tufts.
    if ((this.state.version ?? 0) < 9 && Array.isArray(this.state.weeds)) {
      this.state.weeds = this.state.weeds.filter(() => Math.random() < 0.25);
    }
    // Version 10: sub-cell grid RE-BUMPED from 8×8 to 16×16 because the
    // voxel furrow mesh in Diorama is sized for a 16-wide grid (VOX_W =
    // 1/16); rendering 8 voxels-per-tile left half-empty rows. Double
    // every sub-cell coord so the player's hoed / planted / wet work
    // lines up on the finer grid again. Clamp to 0..15 defensively.
    //
    // Also: dedupe by (c, r, subC, subR). The v7 → v8 halving collapsed
    // distinct coords onto the same cell but never deduped, so older
    // saves carry stale duplicates that inflate the plot progress count
    // past its denominator (e.g. tilled=1334 on a 1024-max plot). Dedup
    // applies to every save passing through v10, so the bug clears even
    // for saves that started at v8 / v9.
    if ((this.state.version ?? 0) < 10) {
      const dbl = (n: number) => Math.max(0, Math.min(15, n * 2));
      const dedup = <T extends { c: number; r: number; subC: number; subR: number }>(arr: T[]): T[] => {
        const seen = new Set<string>();
        const out: T[] = [];
        for (const e of arr) {
          const k = `${e.c}:${e.r}:${e.subC}:${e.subR}`;
          if (seen.has(k)) continue;
          seen.add(k);
          out.push(e);
        }
        return out;
      };
      for (const p of this.state.plots) {
        for (const e of p.tilled) { e.subC = dbl(e.subC); e.subR = dbl(e.subR); }
        for (const e of p.seeds)  { e.subC = dbl(e.subC); e.subR = dbl(e.subR); }
        for (const e of p.wet)    { e.subC = dbl(e.subC); e.subR = dbl(e.subR); }
        p.tilled = dedup(p.tilled);
        p.seeds  = dedup(p.seeds);
        p.wet    = dedup(p.wet);
      }
      for (const w of this.state.weeds) { w.subC = dbl(w.subC); w.subR = dbl(w.subR); }
      this.state.weeds = dedup(this.state.weeds);
      if (Array.isArray(this.state.freeTilled)) {
        for (const e of this.state.freeTilled) { e.subC = dbl(e.subC); e.subR = dbl(e.subR); }
        this.state.freeTilled = dedup(this.state.freeTilled);
      }
      if (Array.isArray(this.state.freeSeeds)) {
        for (const e of this.state.freeSeeds)  { e.subC = dbl(e.subC); e.subR = dbl(e.subR); }
        this.state.freeSeeds = dedup(this.state.freeSeeds);
      }
      if (Array.isArray(this.state.freeWet)) {
        for (const e of this.state.freeWet)    { e.subC = dbl(e.subC); e.subR = dbl(e.subR); }
        this.state.freeWet = dedup(this.state.freeWet);
      }
    }
    // Version 11: dedupe-only pass for saves that already crossed v10
    // without the dedup change shipping (e.g. the user reported a
    // 1334-tilled plot on a 1024-max grid). Re-run dedupe so the count
    // matches the denominator.
    if ((this.state.version ?? 0) < 11) {
      const dedup2 = <T extends { c: number; r: number; subC: number; subR: number }>(arr: T[]): T[] => {
        const seen = new Set<string>();
        const out: T[] = [];
        for (const e of arr) {
          const k = `${e.c}:${e.r}:${e.subC}:${e.subR}`;
          if (seen.has(k)) continue;
          seen.add(k);
          out.push(e);
        }
        return out;
      };
      for (const p of this.state.plots) {
        p.tilled = dedup2(p.tilled);
        p.seeds  = dedup2(p.seeds);
        p.wet    = dedup2(p.wet);
      }
      this.state.weeds = dedup2(this.state.weeds);
      if (Array.isArray(this.state.freeTilled)) this.state.freeTilled = dedup2(this.state.freeTilled);
      if (Array.isArray(this.state.freeSeeds))  this.state.freeSeeds  = dedup2(this.state.freeSeeds);
      if (Array.isArray(this.state.freeWet))    this.state.freeWet    = dedup2(this.state.freeWet);
    }
    // Version 12: real-world calendar. No data migration needed —
    // state.time is unchanged; only the *interpretation* of the day
    // counter is different (seasons now span 3 months instead of 7
    // days, day-of-week tracking added, year counter added).
    // Version bump stamps the save so old code paths can detect it.
    if ((this.state.version ?? 0) < 12) {
      // no-op
    }
    this.state.version = 12;
  }

  /** Pointer to the currently-active plot session, or null. */
  private active(): { tiles: { c: number; r: number }[]; tilled: any[]; seeds: any[]; wet: any[] } | null {
    const i = this.state.activePlotIdx;
    if (i == null || i < 0 || i >= this.state.plots.length) return null;
    return this.state.plots[i];
  }

  /** Find which plot (if any) owns the given tile. Returns the plot session
   *  or null when the tile is free (not in any adopted plot). */
  private plotContaining(c: number, r: number): { tiles: { c: number; r: number }[]; tilled: any[]; seeds: any[]; wet: any[] } | null {
    for (const p of this.state.plots) {
      if (p.tiles.some(t => t.c === c && t.r === r)) return p;
    }
    return null;
  }

  /** Resolve the sub-cell containers for a tile. If the tile is in an
   *  adopted plot, returns that plot's arrays; otherwise returns the world
   *  `free*` arrays. This is the routing primitive that lets every sub-cell
   *  helper work in BOTH plot and overview mode without branching. */
  private subContainer(c: number, r: number): {
    tilled: { c: number; r: number; subC: number; subR: number; stage: number }[];
    seeds: { c: number; r: number; subC: number; subR: number;
             type: SeedType; stage: number; timer: number;
             ox: number; oz: number; growMul: number }[];
    wet: { c: number; r: number; subC: number; subR: number; stage: number; age?: number }[];
  } {
    const plot = this.plotContaining(c, r);
    if (plot) return { tilled: plot.tilled, seeds: plot.seeds, wet: plot.wet };
    return {
      tilled: this.state.freeTilled,
      seeds:  this.state.freeSeeds,
      wet:    this.state.freeWet,
    };
  }

  /** Seed world-wide weeds — every cleared grass tile gets 4–10 random
   *  weed tufts. Called once on first launch (when state.weeds is empty). */
  private seedWorldWeeds() {
    // Sparse weed coverage — most tiles get zero weeds, the ones that do
    // get a single tuft. The dense lawn carpet of weeds was overwhelming;
    // we want weeds to feel like an occasional chore, not a forest floor.
    for (let r = 0; r < this.state.plot.tiles.length; r++) {
      for (let c = 0; c < this.state.plot.tiles[r].length; c++) {
        const t = this.state.plot.tiles[r][c];
        if (t.surf !== 'grass') continue;
        if (t.bld || t.thing) continue;
        // Only ~14% of eligible tiles get any weeds at all.
        if (Math.random() > 0.14) continue;
        // 80% chance of 1 tuft, 20% chance of 2 — never more than that.
        const count = Math.random() < 0.80 ? 1 : 2;
        const used = new Set<string>();
        for (let i = 0; i < count; i++) {
          const subC = Math.floor(Math.random() * PLOT_SUB_DIM);
          const subR = Math.floor(Math.random() * PLOT_SUB_DIM);
          const k = `${subC}:${subR}`;
          if (used.has(k)) continue;
          used.add(k);
          this.state.weeds.push({ c, r, subC, subR });
        }
      }
    }
  }

  /** Transient pause flag — UI sets this when a modal (Settings) is
   *  open. Time, crops, truck travel, energy regen, etc. all freeze;
   *  visuals + camera keep animating because they're driven by the
   *  renderer's own clock (Diorama.update), not by sim.tick.
   *  Intentionally NOT persisted in SimState — reloading should never
   *  start the game paused. */
  paused = false;

  tick(dt: number) {
    if (this.paused) return;
    this.state.time += dt;
    EnergySystem.tick(this.state, dt);
    CropSystem.tick(this.state, dt, this.bus);
    CropSystem.tickActivePlot(this.state, dt, this.bus);
    TreeSystem.tick(this.state, dt, this.bus);
    MillSystem.tick(this.state, dt, this.bus);
    TruckSystem.tick(this.state, dt, this.bus);
    MailboxSystem.tick(this.state, dt, this.bus);
    this.tickWeedRegrowth(dt);
    this.tickFreeArrayCull(dt);
  }

  /** Periodic cull of the free* arrays. Sub-cell tilled/seeded/wet
   *  records on tiles that no longer make sense (built over, became
   *  road, the tile vanished) survive forever otherwise and CropSystem
   *  walks every entry every frame — RAM + CPU compounding bug.
   *
   *  Runs every CULL_INTERVAL seconds, scans the arrays once. Cheap. */
  private freeCullTimer = 0;
  private tickFreeArrayCull(dt: number) {
    const CULL_INTERVAL = 30.0;
    this.freeCullTimer += dt;
    if (this.freeCullTimer < CULL_INTERVAL) return;
    this.freeCullTimer = 0;
    const tile = (c: number, r: number) => PlotSystem.tile(this.state, c, r);
    const survives = (c: number, r: number): boolean => {
      const t = tile(c, r);
      if (!t) return false;
      if (t.bld) return false;
      if (t.surf === 'road') return false;
      return true;
    };
    const filterArr = <T extends { c: number; r: number }>(arr: T[]): T[] => {
      let kept = 0;
      for (let i = 0; i < arr.length; i++) {
        if (survives(arr[i].c, arr[i].r)) {
          if (i !== kept) arr[kept] = arr[i];
          kept++;
        }
      }
      arr.length = kept;
      return arr;
    };
    filterArr(this.state.freeTilled);
    filterArr(this.state.freeSeeds);
    filterArr(this.state.freeWet);
  }

  /** Slow weed regeneration — keeps the world from emptying out once the
   *  player has plucked everything. Every WEED_REGROW_INTERVAL seconds we
   *  try to seed ONE new weed on a random eligible grass sub-cell. Caps
   *  the total weed count at WEED_MAX so the field never re-floods. */
  private weedRegrowTimer = 0;
  private tickWeedRegrowth(dt: number) {
    const WEED_REGROW_INTERVAL = 25.0;     // game-seconds between attempts
    const WEED_MAX = 40;
    this.weedRegrowTimer += dt;
    if (this.weedRegrowTimer < WEED_REGROW_INTERVAL) return;
    this.weedRegrowTimer = 0;
    if ((this.state.weeds?.length ?? 0) >= WEED_MAX) return;
    // Try a handful of random tiles; bail if none are eligible this tick.
    for (let attempt = 0; attempt < 8; attempt++) {
      const rows = this.state.plot.tiles;
      if (!rows.length) return;
      const r = Math.floor(Math.random() * rows.length);
      const row = rows[r];
      if (!row || !row.length) continue;
      const c = Math.floor(Math.random() * row.length);
      const tile = row[c];
      if (!tile || tile.surf !== 'grass') continue;
      if (tile.bld || tile.thing) continue;
      const subC = Math.floor(Math.random() * PLOT_SUB_DIM);
      const subR = Math.floor(Math.random() * PLOT_SUB_DIM);
      // Skip if the sub-cell is tilled, already has a weed, or already
      // hosts a seed — only sprout on bare grass.
      const tilledHere = (this.subContainer(c, r).tilled ?? [])
        .some(s => s.c === c && s.r === r && s.subC === subC && s.subR === subR);
      if (tilledHere) continue;
      if (this.state.weeds.some(w => w.c === c && w.r === r && w.subC === subC && w.subR === subR)) continue;
      this.state.weeds.push({ c, r, subC, subR });
      this.bus.emit({ type: 'weedSpawned', col: c, row: r, subC, subR });
      return;
    }
  }

  // ── Tool / mode setters ───────────────────────────────────────────
  setTool(id: ToolId) {
    if (!this.state.prog.unlocked[id] && id !== 'hands') {
      this.bus.emit({ type: 'blocked', reason: 'Tool not unlocked yet' });
      return;
    }
    this.state.tool = id;
    // Leaving the plant tool also clears any special-seed routing AND the
    // legacy world-overview placingBerry mode. Otherwise the next click
    // could plant a berry under a hands cursor.
    if (id !== 'plant') {
      this.state.selectedSpecialSeed = null;
      this.state.placingBerry = false;
    }
    this.bus.emit({ type: 'stateChanged' });
  }

  setSelectedSeed(seed: SeedType) {
    this.state.selectedSeed = seed;
    // Picking a regular crop seed cancels the special (berry/acorn) routing.
    this.state.selectedSpecialSeed = null;
    this.bus.emit({ type: 'stateChanged' });
  }

  /** Pick berry or acorn as the plant-tool target. Tile clicks become
   *  tile-level placements (bush-sapling / tree-oak-sapling) instead of
   *  the usual sub-cell crop drag-paint. Pass `null` to clear. */
  setSelectedSpecialSeed(kind: 'berry' | 'acorn' | null) {
    this.state.selectedSpecialSeed = kind;
    this.bus.emit({ type: 'stateChanged' });
  }

  // ── Building placement ────────────────────────────────────────────
  selectBuilding(type: BuildingType | null) {
    this.state.selectedBuilding = type;
    this.bus.emit({ type: 'stateChanged' });
  }

  enterPlacementMode() {
    const type = this.state.selectedBuilding;
    if (!type) { this.bus.emit({ type: 'blocked', reason: 'Pick a building first' }); return; }
    if (!EconomySystem.canAfford(this.state, type)) {
      this.bus.emit({ type: 'blocked', reason: 'Cannot afford that — check the cost.' });
      return;
    }
    this.state.placing = { type };
    this.bus.emit({ type: 'stateChanged' });
    this.bus.emit({ type: 'hint', text: 'Click a cleared tile to place the building. ESC to cancel.' });
  }

  cancelPlacement() {
    if (!this.state.placing) return;
    this.state.placing = null;
    this.bus.emit({ type: 'stateChanged' });
  }

  confirmPlacement(c: number, r: number): boolean {
    const placing = this.state.placing;
    if (!placing) return false;
    if (!EconomySystem.placeBuilding(this.state, c, r, placing.type, this.bus)) return false;
    this.state.placing = null;
    this.bus.emit({ type: 'stateChanged' });
    return true;
  }

  /** Demolish the building containing (c, r). Refunds 50% of its cost.
   *  Only player-built granaries/mills can be demolished — mailbox/depot are fixtures. */
  demolish(c: number, r: number): boolean {
    return EconomySystem.demolishBuilding(this.state, c, r, this.bus);
  }

  /** Pay for and advance the construction at (c, r) by one stage.
   *  All-or-nothing; emits "Missing: X" hint if short. Use
   *  depositForBuilding() for the partial-deposit / crate flow. */
  advanceConstruction(c: number, r: number): boolean {
    return ConstructionSystem.advance(this.state, c, r, this.bus);
  }
  /** Deposit whatever materials the player has toward the next stage.
   *  Partial deposits are allowed — the materials sit in the building's
   *  on-site crate until the cost is fully covered. Auto-advances the
   *  stage when the crate's contents match the stage cost. */
  depositForBuilding(c: number, r: number): boolean {
    return ConstructionSystem.depositForNextStage(this.state, c, r, this.bus);
  }

  // ── Plot mode (the focused-farming mini-game shell) ────────────────
  /** Open the zones manager. From here the player creates new plots
   *  and edits tile membership of existing ones. Closes plot view if
   *  it's active so the manager owns the screen. */
  enterZonesMode() {
    if (this.state.zonesMode) return;
    if (this.state.plotInView) this.exitPlot();
    this.state.zonesMode = true;
    this.state.zonesEditPlotIdx = null;
    this.state.selecting = null;
    this.bus.emit({ type: 'zonesModeChanged' });
  }
  exitZonesMode() {
    if (!this.state.zonesMode) return;
    this.state.zonesMode = false;
    this.state.zonesEditPlotIdx = null;
    if (this.state.selecting) {
      this.state.selecting = null;
      this.bus.emit({ type: 'plotSelectChanged' });
    }
    this.bus.emit({ type: 'zonesModeChanged' });
  }
  /** Inside zones mode, pick which existing plot the next tile click
   *  edits. Pass null to deselect (browse-only). */
  selectPlotForEdit(idx: number | null) {
    if (!this.state.zonesMode) return;
    if (idx != null && (idx < 0 || idx >= this.state.plots.length)) return;
    // Picking a plot to edit cancels any in-flight new-plot selection.
    if (idx != null && this.state.selecting) {
      this.state.selecting = null;
      this.bus.emit({ type: 'plotSelectChanged' });
    }
    this.state.zonesEditPlotIdx = idx;
    this.bus.emit({ type: 'zonesModeChanged' });
  }
  /** Add or remove (c, r) from plot `idx`. Removing the last tile drops
   *  the plot entirely. Routes per-sub-cell work in/out of free arrays
   *  the same way confirmPlot + removePlotTile do. */
  togglePlotTile(idx: number, c: number, r: number): boolean {
    if (idx < 0 || idx >= this.state.plots.length) return false;
    const plot = this.state.plots[idx];
    const tile = PlotSystem.tile(this.state, c, r);
    if (!tile) return false;
    if (tile.bld || tile.surf === 'road') return false;
    const existing = plot.tiles.findIndex(t => t.c === c && t.r === r);
    if (existing >= 0) {
      // Remove — same migration logic as removePlotTile (work → free).
      plot.tiles.splice(existing, 1);
      const move = <T extends { c: number; r: number }>(src: T[], dst: T[]) => {
        for (let i = src.length - 1; i >= 0; i--) {
          if (src[i].c === c && src[i].r === r) dst.push(src.splice(i, 1)[0]);
        }
      };
      move(plot.tilled, this.state.freeTilled);
      move(plot.seeds,  this.state.freeSeeds);
      move(plot.wet,    this.state.freeWet);
      this.bus.emit({ type: 'plotTileRemoved', col: c, row: r });
      if (plot.tiles.length === 0) {
        this.state.plots.splice(idx, 1);
        if (this.state.zonesEditPlotIdx === idx) this.state.zonesEditPlotIdx = null;
        else if (this.state.zonesEditPlotIdx != null && this.state.zonesEditPlotIdx > idx) this.state.zonesEditPlotIdx--;
        if (this.state.activePlotIdx === idx) this.state.activePlotIdx = null;
        else if (this.state.activePlotIdx != null && this.state.activePlotIdx > idx) this.state.activePlotIdx--;
      }
    } else {
      // Adding a tile that already belongs to a DIFFERENT plot is a no-op
      // (the player should remove it from the other plot first).
      if (this.state.plots.some((p, pi) => pi !== idx && p.tiles.some(t => t.c === c && t.r === r))) {
        this.bus.emit({ type: 'hint', text: 'That tile already belongs to another plot.' });
        return false;
      }
      plot.tiles.push({ c, r });
      // Migrate any free-tile sub-cell work on (c,r) into the plot.
      const sweep = <T extends { c: number; r: number }>(src: T[], dst: T[]): T[] => {
        const keep: T[] = [];
        for (const e of src) (e.c === c && e.r === r ? dst : keep).push(e);
        return keep;
      };
      this.state.freeTilled = sweep(this.state.freeTilled, plot.tilled);
      this.state.freeSeeds  = sweep(this.state.freeSeeds,  plot.seeds);
      this.state.freeWet    = sweep(this.state.freeWet,    plot.wet);
    }
    this.bus.emit({ type: 'plotsChanged' });
    return true;
  }
  /** Delete a plot wholesale — release every tile back to free state.
   *  The per-sub-cell work (tilled / seeds / wet) is preserved by
   *  moving it into the free arrays, so the player's progress and the
   *  visual furrows / crops / wet patches stay on the ground. The
   *  released tiles ride along in the event payload so the diorama
   *  can rebuild their native grass tufts in one pass. */
  deletePlot(idx: number) {
    if (idx < 0 || idx >= this.state.plots.length) return;
    const plot = this.state.plots[idx];
    const releasedTiles = plot.tiles.map(t => ({ c: t.c, r: t.r }));
    // Only migrate sub-cell work for tiles that the player CAN still see
    // worked — i.e. tiles whose surface is field/grass and that aren't
    // built over. Drops orphaned records on built / road / removed tiles
    // so freeTilled/freeSeeds/freeWet don't grow unboundedly when plots
    // are created and deleted repeatedly. Same effect for the CropSystem
    // tick which walks these arrays every frame.
    const keepFor = (c: number, r: number): boolean => {
      const t = PlotSystem.tile(this.state, c, r);
      if (!t) return false;
      if (t.bld) return false;
      if (t.surf === 'road') return false;
      return true;
    };
    for (const e of plot.tilled) if (keepFor(e.c, e.r)) this.state.freeTilled.push(e);
    for (const e of plot.seeds)  if (keepFor(e.c, e.r)) this.state.freeSeeds.push(e);
    for (const e of plot.wet)    if (keepFor(e.c, e.r)) this.state.freeWet.push(e);
    this.state.plots.splice(idx, 1);
    if (this.state.activePlotIdx === idx) {
      this.state.activePlotIdx = null;
      this.state.plotInView = false;
    } else if (this.state.activePlotIdx != null && this.state.activePlotIdx > idx) {
      this.state.activePlotIdx--;
    }
    if (this.state.zonesEditPlotIdx === idx) this.state.zonesEditPlotIdx = null;
    else if (this.state.zonesEditPlotIdx != null && this.state.zonesEditPlotIdx > idx) this.state.zonesEditPlotIdx--;
    this.bus.emit({ type: 'plotsChanged', releasedTiles });
  }
  renamePlot(idx: number, name: string) {
    if (idx < 0 || idx >= this.state.plots.length) return;
    const trimmed = (name ?? '').trim();
    if (!trimmed) return;
    (this.state.plots[idx] as any).name = trimmed;
    this.bus.emit({ type: 'plotsChanged' });
  }
  /** Enter drag-select. The player paints tiles; release confirms. */
  startPlotSelect() {
    if (this.state.plotInView) {
      this.bus.emit({ type: 'hint', text: 'Exit plot mode first to pick a new plot.' });
      return;
    }
    this.state.selecting = { tiles: [] };
    // Picking new-plot tiles takes priority over editing an existing one.
    this.state.zonesEditPlotIdx = null;
    this.bus.emit({ type: 'plotSelectChanged' });
    this.bus.emit({ type: 'hint', text: 'Click tiles to add them to the new plot. Click "Adopt" to save.' });
  }
  /** Add one tile to the in-flight selection (deduped). */
  addToSelection(c: number, r: number) {
    if (!this.state.selecting) return;
    const t = PlotSystem.tile(this.state, c, r);
    if (!t) return;
    if (t.bld || t.surf === 'road') return;                  // skip buildings + road
    const sel = this.state.selecting.tiles;
    if (sel.some(x => x.c === c && x.r === r)) return;
    sel.push({ c, r });
    this.bus.emit({ type: 'plotSelectChanged' });
  }
  cancelPlotSelect() {
    if (!this.state.selecting) return;
    this.state.selecting = null;
    this.bus.emit({ type: 'plotSelectChanged' });
  }
  /** Adopt a new plot. If the selection overlaps any existing plot(s),
   *  MERGE — the new tiles + every overlapping plot's tiles + per-sub-cell
   *  work all collapse into one larger plot. No work is lost. If there's
   *  no overlap, a brand-new PlotSession is appended. */
  confirmPlot(name?: string): boolean {
    const sel = this.state.selecting;
    if (!sel || sel.tiles.length === 0) return false;
    const selKey = new Set(sel.tiles.map(t => `${t.c}:${t.r}`));
    // Find every existing plot that the selection touches.
    const overlapping: number[] = [];
    for (let i = 0; i < this.state.plots.length; i++) {
      if (this.state.plots[i].tiles.some(t => selKey.has(`${t.c}:${t.r}`))) overlapping.push(i);
    }
    const finalName = name && name.trim() ? name.trim() : `Plot ${this.state.plots.length + 1}`;
    let target: { name?: string; tiles: any[]; tilled: any[]; seeds: any[]; wet: any[] };
    if (overlapping.length === 0) {
      target = { name: finalName, tiles: sel.tiles.slice(), tilled: [], seeds: [], wet: [] };
      this.state.plots.push(target);
    } else {
      // Merge into the first overlapping plot, then fold the rest in.
      target = this.state.plots[overlapping[0]];
      const tileSet = new Set(target.tiles.map((t: any) => `${t.c}:${t.r}`));
      for (let oi = 1; oi < overlapping.length; oi++) {
        const other = this.state.plots[overlapping[oi]];
        for (const t of other.tiles) {
          const k = `${t.c}:${t.r}`;
          if (!tileSet.has(k)) { tileSet.add(k); target.tiles.push(t); }
        }
        target.tilled.push(...other.tilled);
        target.seeds.push(...other.seeds);
        target.wet.push(...other.wet);
      }
      // Remove the now-merged "other" plots (descending order so indices stay valid).
      for (let oi = overlapping.length - 1; oi >= 1; oi--) {
        this.state.plots.splice(overlapping[oi], 1);
      }
      // Add the selection's NEW tiles to the merged plot.
      for (const t of sel.tiles) {
        const k = `${t.c}:${t.r}`;
        if (!tileSet.has(k)) { tileSet.add(k); target.tiles.push(t); }
      }
      // Dedup per-sub-cell records — two overlapping plots could both hold a
      // tilled/seeded/wet entry at the same (c, r, subC, subR). Without
      // dedup, the growth tick double-counts and the player gets ghost
      // crops. For `tilled` and `wet` we keep the HIGHER stage so progress
      // isn't lost. For `seeds` we keep both unless they're true duplicates
      // (same type + ox + oz) — overlapping plots sowing the same crop
      // twice on one sub-cell would be visible as a single stalk anyway.
      const tilledKey = (s: { c: number; r: number; subC: number; subR: number }) =>
        `${s.c}:${s.r}:${s.subC}:${s.subR}`;
      const tilledBest = new Map<string, typeof target.tilled[0]>();
      for (const s of target.tilled) {
        const k = tilledKey(s);
        const prev = tilledBest.get(k);
        if (!prev || s.stage > prev.stage) tilledBest.set(k, s);
      }
      target.tilled = [...tilledBest.values()];
      const wetBest = new Map<string, typeof target.wet[0]>();
      for (const w of target.wet) {
        const k = tilledKey(w);
        const prev = wetBest.get(k);
        if (!prev || w.stage > prev.stage) wetBest.set(k, w);
      }
      target.wet = [...wetBest.values()];
      const seedSeen = new Set<string>();
      target.seeds = target.seeds.filter(s => {
        // Identity = sub-cell + type + ox/oz to nearest 0.01 of a sub-cell.
        // Coarse ox/oz key (0.1 granularity instead of 0.01) — same
        // crop type + sub-cell + similar offset reads as a true duplicate
        // and collapses correctly. The previous 0.01 granularity treated
        // ox=0.123 vs 0.124 as distinct and kept both, doubling visible
        // stalks at the same spot on plot-merge.
        const k = `${tilledKey(s)}:${s.type}:${Math.round(s.ox * 10)}:${Math.round(s.oz * 10)}`;
        if (seedSeen.has(k)) return false;
        seedSeen.add(k);
        return true;
      });
      // Apply the new name only if the user typed one; otherwise keep the
      // pre-existing plot's name so merges don't surprise them.
      if (name && name.trim()) target.name = name.trim();
    }
    // Migrate any free-tile sub-cell work whose tile is now inside the
    // adopted plot. The player may have already tilled / seeded / watered
    // on these tiles in overview; we don't want to lose that work when the
    // tile becomes part of a named plot.
    const targetTileSet = new Set(target.tiles.map((t: any) => `${t.c}:${t.r}`));
    const sweep = <T extends { c: number; r: number }>(src: T[], dst: T[]): T[] => {
      const keep: T[] = [];
      for (const e of src) {
        if (targetTileSet.has(`${e.c}:${e.r}`)) dst.push(e);
        else keep.push(e);
      }
      return keep;
    };
    this.state.freeTilled = sweep(this.state.freeTilled, target.tilled);
    this.state.freeSeeds  = sweep(this.state.freeSeeds,  target.seeds);
    this.state.freeWet    = sweep(this.state.freeWet,    target.wet);
    this.state.activePlotIdx = this.state.plots.indexOf(target as any);
    this.state.selecting = null;
    // Adopting via the zones manager stays in the zones list — the
    // player explicitly clicks the new card to dive in. Outside zones
    // mode (legacy direct flow), enter plot view immediately.
    const stayInZones = !!this.state.zonesMode;
    if (!stayInZones) {
      this.state.plotInView = true;
      this.state.tool = 'hands';
    }
    this.bus.emit({ type: 'plotSelectChanged' });
    if (stayInZones) this.bus.emit({ type: 'plotsChanged' });
    else             this.bus.emit({ type: 'plotEntered' });
    this.bus.emit({ type: 'stateChanged' });
    // Weeds are world-wide now — the player will see them in the world
    // already; the hint just tells them to clear inside their new plot.
    const hasWeeds = this.state.weeds.some(w => target.tiles.some((t: any) => t.c === w.c && t.r === w.r));
    this.bus.emit({
      type: 'hint',
      text: hasWeeds
        ? 'Plot adopted. HANDS first — pull weeds inside your plot. Then HOE, SEEDS, WATER.'
        : 'Plot adopted. Grab the HOE to work the soil, then SEEDS, then WATER.',
    });
    return true;
  }

  /** Yank a weed at the given sub-cell. Weeds live world-wide — the player
   *  can only pluck them while a plot is active (gated at the caller), but
   *  the weed array itself is just `state.weeds`. */
  pluckWeed(c: number, r: number, subC: number, subR: number): boolean {
    if (this.outsideActivePlot(c, r)) return false;
    const idx = this.state.weeds.findIndex(w => w.c === c && w.r === r && w.subC === subC && w.subR === subR);
    if (idx < 0) return false;
    this.state.weeds.splice(idx, 1);
    this.bus.emit({ type: 'weedPlucked', col: c, row: r, subC, subR });
    return true;
  }

  /** Hoe a single sub-cell. Works on ANY tile — if the tile is in an
   *  adopted plot, the entry lands in that plot's tilled[]; otherwise it
   *  lands in state.freeTilled[]. */
  tillSubCell(c: number, r: number, subC: number, subR: number): boolean {
    const tile = PlotSystem.tile(this.state, c, r);
    if (!tile) return false;
    // Plot-mode gate: when a plot is adopted the player is "inside" it; clicks
    // outside the plot's tile list should be silent no-ops so dragging the
    // hoe past the plot edge doesn't light up progress bars on world tiles.
    if (this.outsideActivePlot(c, r)) return false;
    if (tile.surf === 'field') return false;             // already tilled
    if (tile.surf !== 'grass') return false;             // road / stone / etc.
    if (tile.bld || tile.thing || tile.crop) return false;
    // Don't till a sub-cell that still has a weed on it — emit a hint so
    // the player knows why the hoe isn't biting. Throttled to once / 1.5s.
    if (this.state.weeds.some(w => w.c === c && w.r === r && w.subC === subC && w.subR === subR)) {
      const now = this.state.time;
      if (now - this.lastWeedHintAt > 1.5) {
        this.bus.emit({ type: 'hint', text: 'Pull weeds first — switch to Hands.' });
        this.lastWeedHintAt = now;
      }
      return false;
    }
    const sub = this.subContainer(c, r);
    // Multi-pass: each "pass" increments stage 1 → 2 → 3. Stage 3 is the
    // fully-tilled furrow; further passes are no-ops.
    const existing = sub.tilled.find(s => s.c === c && s.r === r && s.subC === subC && s.subR === subR);
    let stage: number;
    if (existing) {
      if (existing.stage >= 3) return false;
      existing.stage++;
      stage = existing.stage;
    } else {
      stage = 1;
      sub.tilled.push({ c, r, subC, subR, stage });
    }
    this.bus.emit({ type: 'subCellTilled', col: c, row: r, subC, subR, stage });
    // Auto-fill: when the player has fully tilled ≥60% of the tile's
    // sub-cells (stage 3), top up the remainder for free. Only fires
    // when stage 3 was just reached — partial tills (stage 1/2) don't
    // count toward the threshold.
    if (stage >= 3) this.maybeAutoFillTileTill(sub, c, r);
    // Plot-wide auto-fill: when the entire adopted plot crosses 60% till
    // coverage, finish every tile. Per-tile auto-fill still handles free
    // tiles outside any plot.
    const plot = this.active();
    if (plot && plot.tiles.some(t => t.c === c && t.r === r)) {
      this.maybePlotAutoFillTill(plot);
    }
    return true;
  }

  /** Drop a seed on a fully-tilled (stage 3) sub-cell. Works on ANY tile —
   *  routes the seed entry into the plot's or world-free seed array based
   *  on tile membership. */
  placeSeedSub(c: number, r: number, subC: number, subR: number): boolean {
    // Plot-mode gate: silent no-op outside the active plot.
    if (this.outsideActivePlot(c, r)) return false;
    // Snap to the stride-2 grid (8×8=64 positions per tile). Keeps the
    // planting visibly evenly spaced and caps tile density at SEED_MAX_PER_TILE.
    subC &= ~1;
    subR &= ~1;
    const sub = this.subContainer(c, r);
    // The snapped sub-cell still needs a finished furrow under it. Manual
    // tilling sometimes leaves odd-coord sub-cells worked but the snapped
    // even-coord neighbour bare; reject in that case rather than silently
    // dropping the seed onto un-tilled grass.
    const tilledHere = sub.tilled.find(s => s.c === c && s.r === r && s.subC === subC && s.subR === subR);
    if (!tilledHere || tilledHere.stage < 3) {
      this.emitSeedBlocked('Need a finished furrow to sow a seed.');
      return false;
    }
    // One seed per sub-cell — second attempt is refused. Players sow on
    // a neighbouring sub-cell instead.
    const inThisSubcell = sub.seeds.filter(s =>
      s.c === c && s.r === r && s.subC === subC && s.subR === subR);
    if (inThisSubcell.length >= 1) {
      // Silent — drag-paint sweeps over already-seeded sub-cells routinely
      // and a toast every time would spam.
      return false;
    }
    const tileSeeds = sub.seeds.filter(s => s.c === c && s.r === r).length;
    if (tileSeeds >= SEED_MAX_PER_TILE) {
      this.emitSeedBlocked(`Tile is full (max ${SEED_MAX_PER_TILE} seeds).`);
      return false;
    }
    // Inventory check — consume a seed from selectedSeed.
    const seed = this.state.selectedSeed;
    if ((this.state.res.seeds[seed] ?? 0) <= 0) {
      this.emitSeedBlocked(`No ${seed} seeds — order some at the mailbox.`);
      return false;
    }
    this.state.res.seeds[seed]--;
    // Per-sub-cell crop with its own stage + timer. Initial timer is the
    // first stage's duration. ox/oz scatter the visual within the sub-cell
    // so a tile of wheats reads as a sprinkle, not a grid. growMul shifts
    // each seed's pace by ±GROW_MUL_JITTER so neighbours ripen at visibly
    // different times.
    const growTimes = CropSystem.growTimes(seed);
    const ox = Math.random() - 0.5;
    const oz = Math.random() - 0.5;
    const growMul = 1 + (Math.random() - 0.5) * 2 * GROW_MUL_JITTER;
    // `timer` stays at the nominal stage duration — CropSystem.tickActivePlot
    // bakes growMul into the per-tick speed, so applying it here too would
    // square the effective rate (caught in QA).
    sub.seeds.push({
      c, r, subC, subR, type: seed, stage: 0, timer: growTimes[0],
      ox, oz, growMul,
    });
    this.bus.emit({ type: 'subCellSeeded', col: c, row: r, subC, subR });
    // Under-seeded hint — fires when this tile has at least one seed but
    // still below the germination threshold, so the player understands why
    // their crops haven't sprouted. Throttled so a drag-paint doesn't spam.
    const newTileCount = tileSeeds + 1;
    if (newTileCount < SEED_MIN_PER_TILE) {
      const now = this.state.time;
      if (now - this.lastSeedHintAt > 2.0) {
        this.bus.emit({
          type: 'hint',
          text: `Need ${SEED_MIN_PER_TILE}+ seeds on this tile to germinate (have ${newTileCount}).`,
        });
        this.lastSeedHintAt = now;
      }
    } else if (newTileCount === SEED_MIN_PER_TILE) {
      this.bus.emit({ type: 'hint', text: 'Seeds will start growing now.' });
    }
    // Auto-fill: when ≥60% of the tile's tilled sub-cells already have a
    // seed, sow the rest with the player's selected seed type (subject
    // to inventory). Skips the threshold check until the just-placed
    // seed is counted in.
    this.maybeAutoFillTileSeed(sub, c, r);
    // Plot-wide auto-fill: same threshold across every tile in the plot.
    const plot = this.active();
    if (plot && plot.tiles.some(t => t.c === c && t.r === r)) {
      this.maybePlotAutoFillSeed(plot);
    }
    return true;
  }

  /** Cut a single horizontal ROW of mature crops at sub-row `subR` across
   *  tile (c, r). One swing now mows the whole strip across the prop
   *  direction instead of the old 3×3 swath. One stalk = one inventory
   *  unit, each deposited via StorageSystem so the basket / granary cap
   *  is honored. Returns true if at least one seed was cut. When the
   *  player has chewed through ≥60% of the plot's originally-ripe seeds
   *  this session, the rest of the plot's stage-4 crops are auto-cut
   *  in the same call (stops on storage-full). */
  cutSubCellCrop(c: number, r: number, subC: number, subR: number): boolean {
    // subC kept in the public signature for caller-compat; the sickle cut
    // now spans the whole subR strip, so the per-column index is unused.
    void subC;
    if (this.outsideActivePlot(c, r)) return false;
    const sub = this.subContainer(c, r);
    // Anchor the sickle session for the active plot on the first cut
    // after plot entry — record how many ripe seeds existed plot-wide so
    // we have a denominator for the 60% auto-harvest check.
    const plot = this.active();
    const plotIdx = this.state.activePlotIdx ?? -1;
    if (plot && plotIdx >= 0 && !this.sickleSession.has(plotIdx)) {
      this.sickleSession.set(plotIdx, this.countRipeInPlot(plot));
    }
    // Walk the targeted ROW and cut every ripe seed in it.
    const cut = this.cutRipeRow(sub, c, r, subR);
    if (cut.count === 0) {
      if (cut.anySeed && !cut.anyRipe) {
        this.bus.emit({ type: 'blocked', reason: 'Not ripe yet — wait for it.' });
      }
      return false;
    }
    // 60%-plot finisher: if cumulative cuts this session have eaten
    // ≥60% of the originally-ripe seed count, sweep the rest.
    if (plot && plotIdx >= 0) {
      const startRipe = this.sickleSession.get(plotIdx) ?? 0;
      const remaining = this.countRipeInPlot(plot);
      if (startRipe > 0 && (startRipe - remaining) / startRipe >= 0.6 && remaining > 0) {
        this.finishHarvestPlot(plot);
        // Reset the session anchor so a second wave (re-ripening) starts fresh.
        this.sickleSession.delete(plotIdx);
      }
    }
    return true;
  }

  /** Cut every ripe seed in one sub-row of a tile. Returns the cut count
   *  plus diagnostic flags for the "nothing ripe" toast. Splits out of
   *  cutSubCellCrop so the plot-finisher can re-use the same loop. */
  private cutRipeRow(
    sub: { seeds: any[] },
    c: number, r: number, subR: number,
  ): { count: number; anySeed: boolean; anyRipe: boolean } {
    let count = 0;
    let anySeed = false;
    let anyRipe = false;
    for (let i = sub.seeds.length - 1; i >= 0; i--) {
      const s = sub.seeds[i];
      if (s.c !== c || s.r !== r || s.subR !== subR) continue;
      anySeed = true;
      if (s.stage < 4) continue;
      anyRipe = true;
      if (StorageSystem.remaining(this.state) <= 0) {
        this.bus.emit({ type: 'blocked', reason: 'Storage full — sell or build a Granary!' });
        break;
      }
      const sType = s.type as SeedType;
      const drop = StorageSystem.deposit(this.state, c, r, sType);
      if (!drop.ok) break;
      // Real-life parity: a ripe head of grain contains its own seed for
      // the next cycle. Award one seed back so the cycle self-sustains.
      this.state.res.seeds[sType] = (this.state.res.seeds[sType] ?? 0) + 1;
      this.bus.emit({ type: 'seedsDelivered', seed: sType, qty: 1 });
      this.bus.emit({ type: 'subCellHarvested', col: c, row: r, subC: s.subC, subR: s.subR, cropType: sType });
      ProgressionSystem.addXp(this.state, XP_REWARDS.harvest, this.bus);
      sub.seeds.splice(i, 1);
      count++;
    }
    return { count, anySeed, anyRipe };
  }

  /** Count stage-4 (ripe) seeds in a plot — used by the 60% auto-harvest. */
  private countRipeInPlot(plot: { seeds: any[] }): number {
    let n = 0;
    for (const s of plot.seeds) if (s.stage >= 4) n++;
    return n;
  }

  /** Auto-cut every remaining ripe seed in the plot. Stops on storage-full
   *  (the per-row helper already emits a blocked hint). Emits a single
   *  plotAutoCompleted with mode='seed' so the HUD can fire a celebratory
   *  burst — re-uses the seed mode tag (HUD interprets). */
  private finishHarvestPlot(plot: { tiles: { c: number; r: number }[]; seeds: any[] }) {
    const plotIdx = this.state.activePlotIdx;
    if (plotIdx == null) return;
    let totalCut = 0;
    // Walk every sub-row of every plot tile and run the row-cut helper.
    // Stops as soon as storage fills (helper bails out).
    for (const t of plot.tiles) {
      for (let subR = 0; subR < PLOT_SUB_DIM; subR++) {
        if (StorageSystem.remaining(this.state) <= 0) break;
        const res = this.cutRipeRow({ seeds: plot.seeds }, t.c, t.r, subR);
        totalCut += res.count;
      }
      if (StorageSystem.remaining(this.state) <= 0) break;
    }
    if (totalCut > 0) {
      this.bus.emit({ type: 'plotAutoCompleted', plotIdx, mode: 'seed' });
    }
  }

  /** Wet a sub-cell. Each pass: 1 damp → 2 moist → 3 soaked. Each
   *  sub-cell retains its wetness independently — no tile aggregation. */
  waterSub(c: number, r: number, subC: number, subR: number): boolean {
    if (this.outsideActivePlot(c, r)) return false;
    const sub = this.subContainer(c, r);
    // 3×3 splash radius — wider range than a single sub-cell so the can
    // actually feels like a watering can.
    const RADIUS = 1;
    let touched = false;
    for (let dr = -RADIUS; dr <= RADIUS; dr++) {
      for (let dc = -RADIUS; dc <= RADIUS; dc++) {
        const sc = subC + dc;
        const sr = subR + dr;
        if (sc < 0 || sr < 0 || sc >= PLOT_SUB_DIM || sr >= PLOT_SUB_DIM) continue;
        if (this.waterOneSub(sub, c, r, sc, sr)) touched = true;
      }
    }
    // Auto-fill: if ≥60% of the tile's tilled sub-cells are already wet
    // (stage ≥ 1), top up the rest so the whole tile reads as watered.
    if (touched) this.maybeAutoFillTileWater(sub, c, r);
    // Plot-wide auto-fill — same threshold across the whole plot.
    if (touched) {
      const plot = this.active();
      if (plot && plot.tiles.some(t => t.c === c && t.r === r)) {
        this.maybePlotAutoFillWater(plot);
      }
    }
    return touched;
  }

  /** Water a single sub-cell (no swath). Multi-pass: 1 damp → 2 moist →
   *  3 soaked. Re-watering an already-soaked cell is a no-op. */
  private waterOneSub(
    sub: { tilled: any[]; wet: any[] },
    c: number, r: number, subC: number, subR: number,
  ): boolean {
    const tilledHere = sub.tilled.find((s: any) => s.c === c && s.r === r && s.subC === subC && s.subR === subR);
    if (!tilledHere || tilledHere.stage < 3) return false;
    const existingW = sub.wet.find((s: any) => s.c === c && s.r === r && s.subC === subC && s.subR === subR);
    let wStage: number;
    if (existingW) {
      if (existingW.stage >= 3) return false;
      existingW.stage++;
      existingW.age = 0;
      wStage = existingW.stage;
    } else {
      wStage = 1;
      sub.wet.push({ c, r, subC, subR, stage: wStage, age: 0 });
    }
    this.bus.emit({ type: 'subCellWatered', col: c, row: r, subC, subR, stage: wStage });
    return true;
  }

  /** When the tile crosses the 60% watered threshold, fill the rest so
   *  the player sees the whole tile read as soaked without having to
   *  chase every last sub-cell. */
  private maybeAutoFillTileWater(
    sub: { tilled: any[]; wet: any[] },
    c: number, r: number,
  ) {
    const tilledHere = sub.tilled.filter((s: any) => s.c === c && s.r === r && s.stage >= 3);
    if (tilledHere.length === 0) return;
    // Map of existing wet entries by sub-cell key so we can both detect
    // coverage AND bump under-saturated entries up to "soaked" (stage 3).
    const wetMap = new Map<string, any>();
    for (const w of sub.wet) {
      if (w.c === c && w.r === r) wetMap.set(`${w.subC}:${w.subR}`, w);
    }
    const wetCount = tilledHere.filter((t: any) => {
      const w = wetMap.get(`${t.subC}:${t.subR}`);
      return w && w.stage >= 1;
    }).length;
    if (wetCount / tilledHere.length < 0.6) return;
    let filled = 0;
    // Walk every tilled sub-cell: insert missing wet entries at stage 3,
    // and bump existing damp/moist ones up to soaked. That way the
    // visual reads as fully watered, not as a patchwork of damps.
    for (const t of tilledHere) {
      const k = `${t.subC}:${t.subR}`;
      const existing = wetMap.get(k);
      if (existing) {
        if (existing.stage >= 3) continue;
        existing.stage = 3;
        existing.age = 0;
      } else {
        sub.wet.push({ c, r, subC: t.subC, subR: t.subR, stage: 3, age: 0 });
      }
      this.bus.emit({ type: 'subCellWatered', col: c, row: r, subC: t.subC, subR: t.subR, stage: 3 });
      filled++;
    }
    if (filled > 0) this.bus.emit({ type: 'tileAutoCompleted', col: c, row: r, mode: 'water' });
  }

  /** Till-side auto-fill — once ≥60% of the tile's sub-cells are at
   *  stage 3 (fully tilled), bump everything else up to stage 3 too.
   *  Skips sub-cells blocked by weeds. */
  private maybeAutoFillTileTill(
    sub: { tilled: any[] },
    c: number, r: number,
  ) {
    const total = PLOT_SUB_DIM * PLOT_SUB_DIM;
    const tilledMap = new Map<string, { stage: number; entry: any }>();
    for (const e of sub.tilled) {
      if (e.c === c && e.r === r) tilledMap.set(`${e.subC}:${e.subR}`, { stage: e.stage, entry: e });
    }
    // Threshold counts ANY sub-cell that's been hoed at all (stage ≥ 1).
    // The first scratch already visually reads as tilled, so the bar
    // should match that. Auto-fill itself still pushes everything to
    // stage 3 below, so 100% means a fully-furrowed tile.
    let covered = 0;
    for (const v of tilledMap.values()) if (v.stage >= 1) covered++;
    if (covered / total < 0.6) return;
    // Already at 100% only when every sub-cell is at the final stage 3,
    // so re-entry after a half-stage auto-fill still lets the player
    // see the burst when it triggers.
    let fullyFinished = 0;
    for (const v of tilledMap.values()) if (v.stage >= 3) fullyFinished++;
    if (fullyFinished >= total) return;
    // Auto-fill is a "finishing" reward — wipe lingering weeds on this
    // tile too so the dirt reads visually clean edge-to-edge. Player still
    // needs to pull weeds manually in the normal flow before reaching the
    // 60% threshold; this just handles the tail.
    const weedIndicesByKey = new Map<string, number>();
    for (let i = this.state.weeds.length - 1; i >= 0; i--) {
      const w = this.state.weeds[i];
      if (w.c === c && w.r === r) weedIndicesByKey.set(`${w.subC}:${w.subR}`, i);
    }
    // Remove from the END so prior indices stay valid as we splice.
    const sortedWeedIndices = [...weedIndicesByKey.values()].sort((a, b) => b - a);
    for (const idx of sortedWeedIndices) {
      const w = this.state.weeds[idx];
      this.state.weeds.splice(idx, 1);
      this.bus.emit({ type: 'weedPlucked', col: w.c, row: w.r, subC: w.subC, subR: w.subR });
    }
    let filled = 0;
    for (let subR = 0; subR < PLOT_SUB_DIM; subR++) {
      for (let subC = 0; subC < PLOT_SUB_DIM; subC++) {
        const k = `${subC}:${subR}`;
        const existing = tilledMap.get(k);
        if (existing && existing.stage >= 3) continue;
        if (existing) {
          existing.entry.stage = 3;
        } else {
          sub.tilled.push({ c, r, subC, subR, stage: 3 });
        }
        this.bus.emit({ type: 'subCellTilled', col: c, row: r, subC, subR, stage: 3 });
        filled++;
      }
    }
    if (filled > 0) this.bus.emit({ type: 'tileAutoCompleted', col: c, row: r, mode: 'till' });
  }

  /** Seed-side auto-fill — once ≥60% of the tile's tilled sub-cells
   *  have a seed, sow the player's selected seed on the rest (up to
   *  inventory). Adds visual jitter via ox/oz so the bonus sowing
   *  doesn't read as a perfectly aligned grid. */
  private maybeAutoFillTileSeed(
    sub: { tilled: any[]; seeds: any[] },
    c: number, r: number,
  ) {
    // Only the stride-2 sub-cells (even subC + subR) are seed-eligible.
    // 8×8=64 max positions per tile, matching SEED_MAX_PER_TILE.
    const tilledEven = sub.tilled.filter((s: any) =>
      s.c === c && s.r === r && s.stage >= 3 && (s.subC & 1) === 0 && (s.subR & 1) === 0);
    if (tilledEven.length === 0) return;
    const seedKeys = new Set<string>();
    for (const s of sub.seeds) {
      if (s.c === c && s.r === r) seedKeys.add(`${s.subC}:${s.subR}`);
    }
    const seededCount = tilledEven.filter((t: any) => seedKeys.has(`${t.subC}:${t.subR}`)).length;
    if (seededCount / tilledEven.length < 0.6) return;
    if (seededCount >= tilledEven.length) return;
    const seedType = this.state.selectedSeed;
    if ((this.state.res.seeds[seedType] ?? 0) <= 0) return;     // no inventory to use
    const growTimes = CropSystem.growTimes(seedType);
    let placed = 0;
    for (const t of tilledEven) {
      const k = `${t.subC}:${t.subR}`;
      if (seedKeys.has(k)) continue;
      if ((this.state.res.seeds[seedType] ?? 0) <= 0) break;     // ran out of seeds
      this.state.res.seeds[seedType]--;
      const ox = (Math.random() - 0.5);
      const oz = (Math.random() - 0.5);
      const growMul = 1 + (Math.random() - 0.5) * 2 * GROW_MUL_JITTER;
      sub.seeds.push({
        c, r, subC: t.subC, subR: t.subR,
        type: seedType, stage: 0, timer: growTimes[0],
        ox, oz, growMul,
      });
      this.bus.emit({ type: 'subCellSeeded', col: c, row: r, subC: t.subC, subR: t.subR });
      placed++;
    }
    if (placed > 0) this.bus.emit({ type: 'tileAutoCompleted', col: c, row: r, mode: 'seed' });
  }

  /** Per-tile work progress — read by the UI to draw progress bars
   *  above tiles. All three modes return current/total/fraction.
   *  Till denominator = full tile (256 sub-cells); water + seed
   *  denominator = the tile's fully-tilled (stage 3) sub-cell count. */
  tileTillProgress(c: number, r: number): { current: number; total: number; fraction: number } {
    // Count any sub-cell with a furrow on it — stage 1 already looks
    // visually tilled, so the bar matches what the player sees on the
    // ground. Matches the threshold logic in maybeAutoFillTileTill.
    const total = PLOT_SUB_DIM * PLOT_SUB_DIM;
    const sub = this.subContainer(c, r);
    let covered = 0;
    for (const e of sub.tilled) if (e.c === c && e.r === r && e.stage >= 1) covered++;
    return { current: covered, total, fraction: total > 0 ? covered / total : 0 };
  }
  tileWaterProgress(c: number, r: number): { current: number; total: number; fraction: number } {
    const sub = this.subContainer(c, r);
    const tilledKeys = new Set<string>();
    for (const e of sub.tilled) if (e.c === c && e.r === r && e.stage >= 3) tilledKeys.add(`${e.subC}:${e.subR}`);
    let wet = 0;
    for (const w of sub.wet) {
      if (w.c === c && w.r === r && w.stage >= 1 && tilledKeys.has(`${w.subC}:${w.subR}`)) wet++;
    }
    const total = tilledKeys.size;
    return { current: wet, total, fraction: total > 0 ? wet / total : 0 };
  }
  tileSeedProgress(c: number, r: number): { current: number; total: number; fraction: number } {
    // Seeds live on the stride-2 grid (even subC + subR) → 64 slots/tile.
    // Denominator = stride-2 tilled-stage-3 cells, so 100% means the tile
    // is densely planted on its 8×8 planting grid.
    const sub = this.subContainer(c, r);
    const tilledKeys = new Set<string>();
    for (const e of sub.tilled) {
      if (e.c !== c || e.r !== r || e.stage < 3) continue;
      if ((e.subC & 1) !== 0 || (e.subR & 1) !== 0) continue;
      tilledKeys.add(`${e.subC}:${e.subR}`);
    }
    let seeded = 0;
    for (const s of sub.seeds) {
      if (s.c === c && s.r === r && tilledKeys.has(`${s.subC}:${s.subR}`)) seeded++;
    }
    const total = tilledKeys.size;
    return { current: seeded, total, fraction: total > 0 ? seeded / total : 0 };
  }

  // ── Plot-wide progress + helpers ──────────────────────────────────
  /** True when a plot is adopted AND (c, r) is outside its tile list.
   *  Used by every per-sub-cell mutator to silently no-op clicks that
   *  drag past the plot's edge while plot-mode is active. */
  private outsideActivePlot(c: number, r: number): boolean {
    const plot = this.active();
    if (!plot) return false;
    return !plot.tiles.some(t => t.c === c && t.r === r);
  }

  /** Throttled emitter for the three seed-blocked toasts (no furrow /
   *  sub-cell full / no seeds). One emission per ~2 sim-seconds total. */
  private emitSeedBlocked(reason: string) {
    const now = this.state.time;
    if (now - this.lastSeedBlockedAt < 2.0) return;
    this.lastSeedBlockedAt = now;
    this.bus.emit({ type: 'blocked', reason });
  }

  /** Plot-wide till progress: every plot tile contributes PLOT_SUB_DIM² sub-cells. */
  plotTillProgress(plotIdx: number): { current: number; total: number; fraction: number } {
    const plot = this.plotAt(plotIdx);
    if (!plot) return { current: 0, total: 0, fraction: 0 };
    const tileKeys = new Set(plot.tiles.map(t => `${t.c}:${t.r}`));
    let covered = 0;
    for (const e of plot.tilled) if (tileKeys.has(`${e.c}:${e.r}`) && e.stage >= 1) covered++;
    const total = plot.tiles.length * PLOT_SUB_DIM * PLOT_SUB_DIM;
    return { current: covered, total, fraction: total > 0 ? covered / total : 0 };
  }
  /** Plot-wide water progress over stage-3 tilled sub-cells. */
  plotWaterProgress(plotIdx: number): { current: number; total: number; fraction: number } {
    const plot = this.plotAt(plotIdx);
    if (!plot) return { current: 0, total: 0, fraction: 0 };
    const tileKeys = new Set(plot.tiles.map(t => `${t.c}:${t.r}`));
    const tilledKeys = new Set<string>();
    for (const e of plot.tilled) {
      if (e.stage >= 3 && tileKeys.has(`${e.c}:${e.r}`)) tilledKeys.add(`${e.c}:${e.r}:${e.subC}:${e.subR}`);
    }
    let wet = 0;
    for (const w of plot.wet) {
      if (w.stage >= 1 && tilledKeys.has(`${w.c}:${w.r}:${w.subC}:${w.subR}`)) wet++;
    }
    const total = tilledKeys.size;
    return { current: wet, total, fraction: total > 0 ? wet / total : 0 };
  }
  /** Plot-wide seed progress over the stride-2 planting grid (8×8 per
   *  tile). 100% = every even-subC, even-subR tilled cell has a seed. */
  plotSeedProgress(plotIdx: number): { current: number; total: number; fraction: number } {
    const plot = this.plotAt(plotIdx);
    if (!plot) return { current: 0, total: 0, fraction: 0 };
    const tileKeys = new Set(plot.tiles.map(t => `${t.c}:${t.r}`));
    const tilledKeys = new Set<string>();
    for (const e of plot.tilled) {
      if (e.stage < 3) continue;
      if ((e.subC & 1) !== 0 || (e.subR & 1) !== 0) continue;
      if (tileKeys.has(`${e.c}:${e.r}`)) tilledKeys.add(`${e.c}:${e.r}:${e.subC}:${e.subR}`);
    }
    let seeded = 0;
    for (const s of plot.seeds) {
      if (tilledKeys.has(`${s.c}:${s.r}:${s.subC}:${s.subR}`)) seeded++;
    }
    const total = tilledKeys.size;
    return { current: seeded, total, fraction: total > 0 ? seeded / total : 0 };
  }

  /** Bounds-checked plot lookup — returns the PlotSession or null. */
  private plotAt(idx: number): { tiles: { c: number; r: number }[]; tilled: any[]; seeds: any[]; wet: any[] } | null {
    if (idx < 0 || idx >= this.state.plots.length) return null;
    return this.state.plots[idx];
  }

  /** Plot-wide till auto-fill: if combined stage-≥1 coverage ≥60% of
   *  (tiles.length × PLOT_SUB_DIM²), push every plot tile to a fully-
   *  tilled state and clear weeds on every plot tile. */
  private maybePlotAutoFillTill(plot: { tiles: { c: number; r: number }[]; tilled: any[] }) {
    const total = plot.tiles.length * PLOT_SUB_DIM * PLOT_SUB_DIM;
    if (total === 0) return;
    const tileKeys = new Set(plot.tiles.map(t => `${t.c}:${t.r}`));
    // Per-(tile,sub) lookup so we can both detect coverage AND bump entries.
    let covered = 0;
    let fullyFinished = 0;
    for (const e of plot.tilled) {
      if (!tileKeys.has(`${e.c}:${e.r}`)) continue;
      if (e.stage >= 1) covered++;
      if (e.stage >= 3) fullyFinished++;
    }
    if (covered / total < 0.6) return;
    if (fullyFinished >= total) return;
    // Build a per-tile map of existing entries so we can bump in place.
    const byKey = new Map<string, any>();
    for (const e of plot.tilled) {
      if (tileKeys.has(`${e.c}:${e.r}`)) byKey.set(`${e.c}:${e.r}:${e.subC}:${e.subR}`, e);
    }
    let filled = 0;
    for (const t of plot.tiles) {
      // Clear every weed on this tile — auto-fill is the finish reward.
      for (let i = this.state.weeds.length - 1; i >= 0; i--) {
        const w = this.state.weeds[i];
        if (w.c === t.c && w.r === t.r) {
          this.state.weeds.splice(i, 1);
          this.bus.emit({ type: 'weedPlucked', col: w.c, row: w.r, subC: w.subC, subR: w.subR });
        }
      }
      for (let sR = 0; sR < PLOT_SUB_DIM; sR++) {
        for (let sC = 0; sC < PLOT_SUB_DIM; sC++) {
          const k = `${t.c}:${t.r}:${sC}:${sR}`;
          const existing = byKey.get(k);
          if (existing) {
            if (existing.stage >= 3) continue;
            existing.stage = 3;
          } else {
            plot.tilled.push({ c: t.c, r: t.r, subC: sC, subR: sR, stage: 3 });
          }
          this.bus.emit({ type: 'subCellTilled', col: t.c, row: t.r, subC: sC, subR: sR, stage: 3 });
          filled++;
        }
      }
      this.bus.emit({ type: 'tileAutoCompleted', col: t.c, row: t.r, mode: 'till' });
    }
    if (filled > 0) {
      const plotIdx = this.state.activePlotIdx ?? -1;
      if (plotIdx >= 0) this.bus.emit({ type: 'plotAutoCompleted', plotIdx, mode: 'till' });
    }
  }

  /** Plot-wide water auto-fill: numerator = wet sub-cells over stage-3
   *  tilled cells across the plot; denominator = stage-3 tilled cells. */
  private maybePlotAutoFillWater(plot: { tiles: { c: number; r: number }[]; tilled: any[]; wet: any[] }) {
    const tileKeys = new Set(plot.tiles.map(t => `${t.c}:${t.r}`));
    const tilledKey = new Map<string, any>();
    for (const e of plot.tilled) {
      if (e.stage >= 3 && tileKeys.has(`${e.c}:${e.r}`)) tilledKey.set(`${e.c}:${e.r}:${e.subC}:${e.subR}`, e);
    }
    const total = tilledKey.size;
    if (total === 0) return;
    const wetByKey = new Map<string, any>();
    for (const w of plot.wet) {
      if (tilledKey.has(`${w.c}:${w.r}:${w.subC}:${w.subR}`)) wetByKey.set(`${w.c}:${w.r}:${w.subC}:${w.subR}`, w);
    }
    if (wetByKey.size / total < 0.6) return;
    let filled = 0;
    const touchedTiles = new Set<string>();
    for (const [k, t] of tilledKey) {
      const existing = wetByKey.get(k);
      if (existing) {
        if (existing.stage >= 3) continue;
        existing.stage = 3;
        existing.age = 0;
      } else {
        plot.wet.push({ c: t.c, r: t.r, subC: t.subC, subR: t.subR, stage: 3, age: 0 });
      }
      this.bus.emit({ type: 'subCellWatered', col: t.c, row: t.r, subC: t.subC, subR: t.subR, stage: 3 });
      touchedTiles.add(`${t.c}:${t.r}`);
      filled++;
    }
    if (filled > 0) {
      for (const tk of touchedTiles) {
        const [tc, tr] = tk.split(':').map(Number);
        this.bus.emit({ type: 'tileAutoCompleted', col: tc, row: tr, mode: 'water' });
      }
      const plotIdx = this.state.activePlotIdx ?? -1;
      if (plotIdx >= 0) this.bus.emit({ type: 'plotAutoCompleted', plotIdx, mode: 'water' });
    }
  }

  /** Plot-wide seed auto-fill: numerator = seeded sub-cells over stage-3
   *  tilled cells across the plot; denominator = stage-3 tilled cells.
   *  Sows the player's selectedSeed and stops on seed-inventory empty. */
  private maybePlotAutoFillSeed(plot: { tiles: { c: number; r: number }[]; tilled: any[]; seeds: any[] }) {
    const tileKeys = new Set(plot.tiles.map(t => `${t.c}:${t.r}`));
    // Stride-2 — only even subC + subR sub-cells are seed-eligible so the
    // plot ends up with an evenly-spaced 8×8 planting grid per tile.
    const tilledKey = new Map<string, any>();
    for (const e of plot.tilled) {
      if (e.stage < 3) continue;
      if ((e.subC & 1) !== 0 || (e.subR & 1) !== 0) continue;
      if (tileKeys.has(`${e.c}:${e.r}`)) tilledKey.set(`${e.c}:${e.r}:${e.subC}:${e.subR}`, e);
    }
    const total = tilledKey.size;
    if (total === 0) return;
    const seedKeys = new Set<string>();
    for (const s of plot.seeds) {
      const k = `${s.c}:${s.r}:${s.subC}:${s.subR}`;
      if (tilledKey.has(k)) seedKeys.add(k);
    }
    if (seedKeys.size / total < 0.6) return;
    if (seedKeys.size >= total) return;
    const seedType = this.state.selectedSeed;
    if ((this.state.res.seeds[seedType] ?? 0) <= 0) return;
    const growTimes = CropSystem.growTimes(seedType);
    let placed = 0;
    const touchedTiles = new Set<string>();
    for (const [k, t] of tilledKey) {
      if (seedKeys.has(k)) continue;
      if ((this.state.res.seeds[seedType] ?? 0) <= 0) break;
      this.state.res.seeds[seedType]--;
      const ox = Math.random() - 0.5;
      const oz = Math.random() - 0.5;
      const growMul = 1 + (Math.random() - 0.5) * 2 * GROW_MUL_JITTER;
      plot.seeds.push({
        c: t.c, r: t.r, subC: t.subC, subR: t.subR,
        type: seedType, stage: 0, timer: growTimes[0],
        ox, oz, growMul,
      });
      this.bus.emit({ type: 'subCellSeeded', col: t.c, row: t.r, subC: t.subC, subR: t.subR });
      touchedTiles.add(`${t.c}:${t.r}`);
      placed++;
    }
    if (placed > 0) {
      for (const tk of touchedTiles) {
        const [tc, tr] = tk.split(':').map(Number);
        this.bus.emit({ type: 'tileAutoCompleted', col: tc, row: tr, mode: 'seed' });
      }
      const plotIdx = this.state.activePlotIdx ?? -1;
      if (plotIdx >= 0) this.bus.emit({ type: 'plotAutoCompleted', plotIdx, mode: 'seed' });
    }
  }

  /** Leave plot VIEW. Plot DATA is preserved — every tilled sub-cell,
   *  seeded wheat, watered patch stays exactly as the player shaped it,
   *  and the per-sub-cell rendering continues in the world overview.
   *  Only the "selected" status (gold overlay, plot-mode camera) clears.
   *  Adopting a new plot via startPlotSelect → confirmPlot REPLACES the
   *  saved plot. To get back into the saved plot, click on any of its
   *  tiles with the hands tool. */
  exitPlot() {
    if (!this.state.plotInView) return;
    this.state.plotInView = false;
    this.state.activePlotIdx = null;
    this.state.editingPlot = false;
    // Drop any sickle session bookkeeping — a fresh entry should re-anchor.
    this.sickleSession.clear();
    this.bus.emit({ type: 'plotExited' });
    if (this.state.tool !== 'hands') {
      this.state.tool = 'hands';
      this.bus.emit({ type: 'stateChanged' });
    }
  }
  /** Re-enter plot view on a saved plot. If plotIdx given, activates that
   *  one; else picks whichever plot contains (c, r) if supplied; else no-op. */
  enterPlotView(plotIdx?: number, c?: number, r?: number): boolean {
    let idx = plotIdx;
    if (idx == null && c != null && r != null) {
      idx = this.state.plots.findIndex(p => p.tiles.some(t => t.c === c && t.r === r));
      if (idx < 0) return false;
    }
    if (idx == null) {
      // Fall back to the most-recently-active or first available plot.
      idx = this.state.activePlotIdx ?? (this.state.plots.length > 0 ? 0 : -1);
    }
    if (idx < 0 || idx >= this.state.plots.length) return false;
    if (this.state.plotInView && this.state.activePlotIdx === idx) return true;
    this.state.activePlotIdx = idx;
    this.state.plotInView = true;
    // Fresh entry into a plot resets the sickle session anchor so the
    // 60% auto-harvest measures THIS visit, not a previous one.
    this.sickleSession.delete(idx);
    this.bus.emit({ type: 'plotEntered' });
    this.state.tool = 'hands';
    this.bus.emit({ type: 'stateChanged' });
    return true;
  }
  /** Is (c, r) inside any adopted plot? */
  private inPlot(c: number, r: number): boolean {
    return this.state.plots.some(p => p.tiles.some(t => t.c === c && t.r === r));
  }

  // ── Moveable props (toolrack / seed bag / storage shelf) ──────────
  /** Enter "click a tile to place" mode for one of the world props.
   *  Triggered by the "Reposition" button in the prop's panel. */
  startMoving(kind: PropKind) {
    this.state.moving = { kind };
    this.bus.emit({ type: 'stateChanged' });
    this.bus.emit({ type: 'hint', text: `Click any tile to place the ${kind}. Right-click or ESC to cancel.` });
  }
  cancelMoving() {
    if (!this.state.moving) return;
    this.state.moving = null;
    this.bus.emit({ type: 'stateChanged' });
  }
  /** Commit the in-flight prop relocation to (c, r). Validates the tile. */
  confirmMoveProp(c: number, r: number): boolean {
    const move = this.state.moving;
    if (!move) return false;
    const t = PlotSystem.tile(this.state, c, r);
    if (!t)                  { this.bus.emit({ type: 'blocked', reason: 'Out of bounds.' });        return false; }
    if (t.bld)               { this.bus.emit({ type: 'blocked', reason: 'A building is here.' });   return false; }
    if (t.surf === 'road')   { this.bus.emit({ type: 'blocked', reason: 'Can\'t place on the road.' }); return false; }
    if (!this.state.props) this.state.props = defaultPropLayout();
    this.state.props[move.kind] = { c, r };
    this.bus.emit({ type: 'propMoved', kind: move.kind, col: c, row: r });
    this.state.moving = null;
    this.bus.emit({ type: 'stateChanged' });
    return true;
  }

  // ── Tile interactions ─────────────────────────────────────────────
  clickTile(c: number, r: number): boolean {
    const tile = PlotSystem.tile(this.state, c, r);
    if (!tile) return false;

    // Move-prop mode short-circuits everything else.
    if (this.state.moving) {
      return this.confirmMoveProp(c, r);
    }
    // Placement mode short-circuits everything else.
    if (this.state.placing) {
      return this.confirmPlacement(c, r);
    }
    // Berry-planting mode — drop the next click on a cleared grass tile.
    if (this.state.placingBerry) {
      const ok = TreeSystem.plantBerry(this.state, c, r, this.bus);
      if (ok) {
        this.state.placingBerry = false;
        this.bus.emit({ type: 'stateChanged' });
      }
      return ok;
    }
    // Plot edit-mode — clicking a tile inside the active plot removes that
    // tile from the plot's list (and clears its per-sub-cell work).
    if (this.state.editingPlot && this.state.plotInView) {
      return this.removePlotTile(c, r);
    }

    // Buildings are always interactive — clicking any tile of the footprint
    // opens the building's panel. State (mill batch etc.) lives on the anchor.
    if (tile.bld) {
      const anchor = PlotSystem.buildingAnchor(this.state, c, r);
      if (!anchor) return false;
      const kind =
        anchor.tile.bld === 'granary'     ? 'storage' :
        anchor.tile.bld === 'mill'        ? 'mill' :
        anchor.tile.bld === 'mailbox'     ? 'mailbox' :
        anchor.tile.bld === 'truck-depot' ? 'truck' :
                                            'tile';
      this.bus.emit({ type: 'inspect', kind, col: anchor.c, row: anchor.r });
      // Mill convenience: auto-start a batch when clicked if conditions met.
      if (anchor.tile.bld === 'mill' && !MillSystem.isBusy(anchor.tile)
          && MillSystem.canStart(this.state, anchor.tile)) {
        MillSystem.start(this.state, anchor.c, anchor.r, this.bus);
      }
      return true;
    }

    // Non-building tiles — manual farming tools all go through applyTool so
    // plot-mode gating is enforced in one place. Bulk + axe still dispatch
    // directly (axe doesn't need a plot; bulk is being deprecated anyway).
    const tool = this.state.tool;
    // Hands + ripe forage on this tile → harvest mushrooms / berries.
    // This wins over the "re-enter saved plot" shortcut so foraging works
    // from anywhere in the world (including overview).
    if (tool === 'hands' && TreeSystem.canForage(this.state, c, r)) {
      return TreeSystem.forage(this.state, c, r, this.bus);
    }
    // Hands + click inside an existing saved plot → re-enter THAT plot.
    // The Plot dock button always starts a new selection, so this is the
    // shortcut to resume work on a plot that's already adopted.
    if (tool === 'hands' && !this.state.plotInView && this.inPlot(c, r)) {
      this.enterPlotView(undefined, c, r);
      return true;
    }
    switch (tool) {
      case 'hands':   return false;
      case 'field':
      case 'plant':
      case 'water':
      case 'scythe':  return this.applyTool(c, r);
      case 'seedBag': return CropSystem.bulkPlant(this.state, this.bus) > 0;
      case 'axe':
      case 'pickaxe': return TreeSystem.chop(this.state, c, r, this.bus);
    }
    return false;
  }

  // ── Forage / berry planting / plot edit ───────────────────────────
  /** Toggle "next tile click plants a berry" mode. Cancels other modes. */
  startBerryPlanting() {
    if ((this.state.res.berries ?? 0) <= 0) {
      this.bus.emit({ type: 'blocked', reason: 'No berries to plant.' });
      return;
    }
    this.state.placingBerry = true;
    this.state.placing = null;
    this.bus.emit({ type: 'stateChanged' });
    this.bus.emit({ type: 'hint', text: 'Click a cleared grass tile to plant a berry. ESC to cancel.' });
  }
  cancelBerryPlanting() {
    if (!this.state.placingBerry) return;
    this.state.placingBerry = false;
    this.bus.emit({ type: 'stateChanged' });
  }
  /** Toggle plot edit mode. Only valid while a plot is in view. */
  togglePlotEdit() {
    if (!this.state.plotInView) {
      this.bus.emit({ type: 'blocked', reason: 'Enter a plot first.' });
      return;
    }
    this.state.editingPlot = !this.state.editingPlot;
    if (this.state.editingPlot) {
      this.bus.emit({ type: 'hint', text: 'EDIT MODE — click a tile to remove it from your plot. Click Edit again to finish.' });
    }
    this.bus.emit({ type: 'stateChanged' });
  }
  /** Remove (c, r) from the active plot's tile list and drop any per-sub-cell
   *  work that lived on that tile. If the plot is left empty, exit plot view. */
  removePlotTile(c: number, r: number): boolean {
    const plot = this.active();
    if (!plot) return false;
    const idx = plot.tiles.findIndex(t => t.c === c && t.r === r);
    if (idx < 0) {
      this.bus.emit({ type: 'blocked', reason: 'That tile isn\'t in your plot.' });
      return false;
    }
    // Un-group the tile: drop it from plot.tiles[] AND migrate every
    // per-sub-cell record (tilled/seeds/wet) on that tile out to the
    // world-level free arrays. The player's work survives — the records
    // still tick, still render, and the tile still farms as a free tile.
    // Re-adopting the tile into another plot will sweep the data back in.
    plot.tiles.splice(idx, 1);
    const move = <T extends { c: number; r: number }>(src: T[], dst: T[]) => {
      for (let i = src.length - 1; i >= 0; i--) {
        if (src[i].c === c && src[i].r === r) dst.push(src.splice(i, 1)[0]);
      }
    };
    move(plot.tilled, this.state.freeTilled);
    move(plot.seeds,  this.state.freeSeeds);
    move(plot.wet,    this.state.freeWet);
    this.bus.emit({ type: 'plotTileRemoved', col: c, row: r });
    // Empty plot → drop the session entirely and exit plot view.
    if (plot.tiles.length === 0) {
      const i = this.state.activePlotIdx ?? -1;
      if (i >= 0) this.state.plots.splice(i, 1);
      this.state.activePlotIdx = null;
      this.state.plotInView = false;
      this.state.editingPlot = false;
      this.bus.emit({ type: 'plotExited' });
      this.bus.emit({ type: 'hint', text: 'Plot is empty — closed it for you.' });
    }
    return true;
  }

  /** Apply the currently-selected tool to a single tile without going
   *  through the overview→zoom routing. Used by Diorama's drag-to-till
   *  flow: each tile crossed by the hoe drag commits one placeField call. */
  applyTool(c: number, r: number): boolean {
    const tile = PlotSystem.tile(this.state, c, r);
    if (!tile) return false;
    if (tile.bld) return false;          // never till/plant under a building
    const tool = this.state.tool;
    // Farming tools now work ANYWHERE in the overview — the plot is just
    // a named grouping affordance, not a precondition for farming. The
    // sub-cell drag-paint in Diorama still gates on plot membership for
    // the per-sub-cell mechanic, but tile-level placeField/water/etc.
    // run wherever the player clicks.
    switch (tool) {
      case 'field':  return CropSystem.placeField(this.state, c, r, this.bus);
      case 'plant':  {
        // Special-seed routing: plant tool + berry/acorn picked → place
        // bush-sapling / tree-oak-sapling at tile level on a plot tile.
        if (this.state.selectedSpecialSeed === 'berry') {
          return TreeSystem.plantBerry(this.state, c, r, this.bus);
        }
        if (this.state.selectedSpecialSeed === 'acorn') {
          return TreeSystem.plantAcorn(this.state, c, r, this.bus);
        }
        if (tile.crop && tile.crop.stage >= 4) {
          return CropSystem.harvest(this.state, c, r, this.bus);
        }
        return CropSystem.plant(this.state, c, r, this.bus);
      }
      case 'water':  return CropSystem.water(this.state, c, r, this.bus);
      case 'scythe': return CropSystem.harvest(this.state, c, r, this.bus);
    }
    return false;
  }

  /** Exit the close-up zoom and return the camera to the overview. */
  exitZoom() {
    if (!this.state.zoom?.active) return;
    this.state.zoom = { active: false, c: 0, r: 0 };
    this.bus.emit({ type: 'zoomExited' });
  }

  // ── Truck / mailbox shortcuts (UI calls these) ────────────────────
  loadTruck(kind: CargoKind) {
    return TruckSystem.load(this.state, kind, this.bus);
  }
  loadAllTruck(kind: CargoKind) {
    return TruckSystem.loadAll(this.state, kind, this.bus);
  }
  /** Load one unit toward the truck. If the truck is parked (idle /
   *  loading), the item goes directly onto the cargo bed and immediately
   *  shows up on the truck mesh. If the truck is away (departed /
   *  returning), it's queued on the depot platform and the truck picks
   *  it up on its next return. From the player's POV: the panel "Load"
   *  button always fills the visible truck when possible. */
  sendToPlatform(kind: CargoKind) {
    const t = this.state.truck;
    if (t.status === 'idle' || t.status === 'loading') {
      return TruckSystem.load(this.state, kind, this.bus);
    }
    return TruckSystem.sendToPlatform(this.state, kind, this.bus);
  }
  sendAllToPlatform(kind: CargoKind) {
    const t = this.state.truck;
    if (t.status === 'idle' || t.status === 'loading') {
      return TruckSystem.loadAll(this.state, kind, this.bus);
    }
    return TruckSystem.sendAllToPlatform(this.state, kind, this.bus);
  }
  recallFromPlatform(kind: CargoKind) {
    return TruckSystem.recallFromPlatform(this.state, kind, this.bus);
  }
  dispatchTruck() {
    return TruckSystem.dispatch(this.state, this.bus);
  }
  /** Buy seeds via the truck. The player pays up-front, the order is
   *  queued onto the truck, and the seeds are delivered when the truck
   *  comes back from the city. The truck must be parked (idle / loading
   *  — i.e. not already on the road) for an order to be accepted.
   *  Seeds appear in inventory on truck return, not before. */
  buySeedsDirect(seed: SeedType, qty: number): boolean {
    const t = this.state.truck;
    if (t.status === 'departed' || t.status === 'returning') {
      this.bus.emit({ type: 'blocked', reason: 'Truck is on the road — wait for it to return.' });
      return false;
    }
    const cost = SEED_COSTS[seed] * qty;
    if (this.state.res.money < cost) {
      this.bus.emit({ type: 'blocked', reason: `Need ${cost}c.` });
      return false;
    }
    this.state.res.money -= cost;
    // Queue the order onto the truck. Aggregating same-seed orders keeps
    // the pendingSeeds list short. The truck stays parked — the player
    // must explicitly press Send-to-city to start the trip. This gives
    // them a chance to also load cargo for sale in the same round-trip
    // and avoids surprise dispatches.
    if (!Array.isArray(t.pendingSeeds)) t.pendingSeeds = [];
    const existing = t.pendingSeeds.find(o => o.seed === seed);
    if (existing) existing.qty += qty;
    else t.pendingSeeds.push({ seed, qty });
    this.bus.emit({ type: 'hint',
      text: `+${qty} ${seed} ordered — press Send truck to fetch them (-${cost}c).` });
    this.bus.emit({ type: 'seedsBought', seed, qty, cost });
    this.bus.emit({ type: 'stateChanged' });
    return true;
  }

  /** Buy stone via the truck. Mirrors buySeedsDirect: player pays now, the
   *  truck queues the order and delivers stone to inventory on its return.
   *  Truck must be parked. */
  buyStoneDirect(qty: number): boolean {
    if (qty <= 0) return false;
    const t = this.state.truck;
    if (t.status === 'departed' || t.status === 'returning') {
      this.bus.emit({ type: 'blocked', reason: 'Truck is on the road — wait for it to return.' });
      return false;
    }
    const cost = STONE_BUY_PRICE * qty;
    if (this.state.res.money < cost) {
      this.bus.emit({ type: 'blocked', reason: `Need ${cost}c.` });
      return false;
    }
    this.state.res.money -= cost;
    if (!t.pendingStone) t.pendingStone = { qty: 0, cost: 0 };
    t.pendingStone.qty += qty;
    t.pendingStone.cost += cost;
    this.bus.emit({ type: 'hint',
      text: `+${qty} stone ordered — press Send truck to fetch (-${cost}c).` });
    this.bus.emit({ type: 'stateChanged' });
    return true;
  }
  placeSeedOrder(order: SeedOrderLine[]) {
    return MailboxSystem.placeOrder(this.state, order, this.bus);
  }

  startMill(c: number, r: number): boolean {
    return MillSystem.start(this.state, c, r, this.bus);
  }

  /** Queue `qty` more flour batches of `kind` at the mill at (c, r).
   *  If the mill is idle the head batch starts immediately. */
  enqueueMillBatch(c: number, r: number, kind: 'wheat' | 'corn', qty: number): boolean {
    return MillSystem.enqueueBatch(this.state, c, r, kind, qty, this.bus);
  }

  /** Empty the mill's pending queue. The currently-grinding batch (if any)
   *  finishes anyway so its already-consumed input still pays out. */
  cancelMillQueue(c: number, r: number): boolean {
    return MillSystem.cancelQueue(this.state, c, r, this.bus);
  }

  // ── Helpers re-exported for UI ────────────────────────────────────
  storage() { return StorageSystem.report(this.state); }

  // ── Calendar derivations ──────────────────────────────────────────
  /** Sim-relative time, with mid-morning offset baked in. */
  private rawTime(): number { return this.state.time + TIME_INITIAL_OFFSET; }
  /** 0..1 fraction of the day (0 = midnight, 0.5 = noon). */
  getTimeOfDay(): number {
    return (this.rawTime() / DAY_LENGTH) % 1.0;
  }
  /** 1-based day counter. Day 1 starts at the initial offset. */
  getDay(): number {
    return Math.floor(this.rawTime() / DAY_LENGTH) + 1;
  }

  /**
   * Full calendar position for the current game day. Drives every
   * date-aware UI surface (clock card, time widget) and, once the
   * gameplay layer lands, will gate crop planting / winter death /
   * greenhouse overrides via `MONTH_SEASON` + the forward-looking
   * `CropSeasonality` type in `types.ts`.
   *
   * Day 1 of the save = March 1 of Year 1 (`GAME_START_DOY = 60`).
   * Starting in Spring means a fresh player can immediately plant
   * outdoors — winter-only locks come later when the greenhouse exists.
   */
  getDate(): GameDate {
    const dayAbsolute = this.getDay();
    // Project the absolute day onto the wrapping 365-day calendar,
    // anchored at GAME_START_DOY.
    const projected = (dayAbsolute - 1) + (GAME_START_DOY - 1);
    const year = Math.floor(projected / DAYS_PER_YEAR) + 1;
    const dayOfYear = (projected % DAYS_PER_YEAR) + 1;            // 1..365
    // Find which month this day-of-year falls in via the MONTH_FIRST_DOY
    // lookup table.
    let monthIdx = 0;
    for (let i = MONTHS.length - 1; i >= 0; i--) {
      if (dayOfYear >= MONTH_FIRST_DOY[MONTHS[i]]) { monthIdx = i; break; }
    }
    const month = MONTHS[monthIdx];
    const dayOfMonth = dayOfYear - MONTH_FIRST_DOY[month] + 1;
    // Treat absolute Day 1 of the save as Monday — gives a stable
    // cycle anchor that doesn't depend on year-roll math.
    const dayOfWeek = ((dayAbsolute - 1) % DAYS_PER_WEEK + DAYS_PER_WEEK) % DAYS_PER_WEEK;
    const weekday = WEEKDAYS[dayOfWeek];
    const weekOfYear = Math.floor((dayOfYear - 1) / DAYS_PER_WEEK) + 1;
    const season = MONTH_SEASON[month];
    return {
      dayAbsolute, year, month, monthIdx, dayOfMonth, dayOfYear,
      dayOfWeek, weekday, weekOfYear, season,
    };
  }

  /** Current season, derived from the calendar month. Replaces the
   *  legacy "cycle every 7 days" logic — seasons now span 3 months. */
  getSeason(): Season {
    return this.getDate().season;
  }

  /** Day-of-season (1..~92). Counted from the first day of the
   *  current season's first month. Mostly useful for progress bars. */
  getDayOfSeason(): number {
    const d = this.getDate();
    // Find the first month of the current season.
    const seasonMonths = MONTHS.filter(m => MONTH_SEASON[m] === d.season);
    // seasonMonths are in calendar order but the WINTER trio wraps
    // (dec/jan/feb), so anchor to whichever winter month is "first" by
    // putting december ahead of jan/feb when the current month is one
    // of them.
    let firstMonth: Month = seasonMonths[0];
    if (d.season === 'winter') firstMonth = 'december';
    let count = 0;
    // Walk forward from firstMonth through the season until we hit
    // the current month, summing days. With small N this is fine.
    let cursor = firstMonth;
    const monthLen = (m: Month) => MONTH_DAYS[m];
    const nextMonth = (m: Month): Month =>
      MONTHS[(MONTHS.indexOf(m) + 1) % MONTHS.length];
    let guard = 0;
    while (cursor !== d.month && guard++ < 12) {
      count += monthLen(cursor);
      cursor = nextMonth(cursor);
    }
    return count + d.dayOfMonth;
  }
}
